import { useEffect, useRef, useState } from "react";
import { useSettings } from "../context/SettingsContext";
import { resolveImageUrl } from "../lib/api";

function classNamesForSpan(span, index) {
  // Responsive bento layout — clean on mobile, asymmetric on lg+
  const map = [
    "sm:col-span-2 lg:col-span-7 lg:row-span-2 aspect-[4/5]",
    "sm:col-span-2 lg:col-span-5 lg:row-span-1 aspect-[4/3]",
    "sm:col-span-2 lg:col-span-5 lg:row-span-1 aspect-[4/3]",
    "sm:col-span-1 lg:col-span-4 aspect-[3/4]",
    "sm:col-span-1 lg:col-span-4 aspect-[3/4]",
    "sm:col-span-2 lg:col-span-4 aspect-[3/4]",
  ];
  return map[index % map.length];
}

export default function Gallery({ products, onSelect }) {
  const { settings } = useSettings();
  const [visible, setVisible] = useState(new Set());
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible((prev) => {
              const next = new Set(prev);
              next.add(e.target.dataset.id);
              return next;
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(refs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [products.length]);

  return (
    <section
      id="collezione"
      data-testid="gallery-section"
      className="relative py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            {settings.gallery_eyebrow && settings.gallery_eyebrow.trim() && (
              <p className="label-eyebrow" data-testid="gallery-eyebrow">
                {settings.gallery_eyebrow}
              </p>
            )}
            {(settings.gallery_title_a || settings.gallery_title_b) && (
              <h2
                data-testid="gallery-title"
                className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-6 leading-[0.95]"
              >
                {settings.gallery_title_a}
                {settings.gallery_title_a && settings.gallery_title_b && <br />}
                {settings.gallery_title_b && (
                  <span className="italic text-[color:var(--tr-ink-soft)]">
                    {settings.gallery_title_b}
                  </span>
                )}
              </h2>
            )}
          </div>
          {settings.gallery_text && settings.gallery_text.trim() && (
            <p className="font-body max-w-md text-[color:var(--tr-ink-soft)] leading-relaxed whitespace-pre-wrap">
              {settings.gallery_text}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 sm:gap-6 lg:gap-8 auto-rows-[minmax(0,1fr)]">
          {products.map((p, i) => {
            const isVisible = visible.has(p.id);
            return (
              <button
                key={p.id}
                data-testid={`gallery-item-${p.code}`}
                ref={(el) => (refs.current[p.id] = el)}
                data-id={p.id}
                onClick={() => onSelect(p)}
                className={`group text-left ${classNamesForSpan(p.span, i)}`}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(36px)",
                  transition:
                    "opacity 1100ms cubic-bezier(.25,1,.5,1), transform 1100ms cubic-bezier(.25,1,.5,1)",
                  transitionDelay: `${(i % 3) * 90}ms`,
                }}
                aria-label={`Apri ${p.name}`}
              >
                <div className="tr-img-wrap w-full h-full bg-[color:var(--tr-bg-2)] relative">
                  <img src={resolveImageUrl(p.image)} alt={p.name} loading="lazy" />
                  {/* Full overlay on hover: dark gradient with light text for guaranteed readability */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(26,25,24,0) 35%, rgba(26,25,24,0.55) 70%, rgba(26,25,24,0.85) 100%)",
                    }}
                    aria-hidden="true"
                  />
                  <div className="absolute inset-0 flex items-end p-5 pointer-events-none">
                    <div className="opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 max-w-[92%]">
                      <p
                        className="label-eyebrow"
                        style={{ color: "rgba(248,247,244,0.7)" }}
                      >
                        N° {p.code} · {p.category}
                      </p>
                      <p
                        className="font-display text-2xl sm:text-3xl mt-1 leading-tight"
                        style={{ color: "#F8F7F4" }}
                      >
                        {p.name}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-baseline justify-between mt-4">
                  <span className="font-sub text-[0.78rem] uppercase tracking-[0.24em] text-[color:var(--tr-ink-soft)]">
                    N° {p.code} — {p.category}
                  </span>
                  <span className="font-sub text-[0.78rem] uppercase tracking-[0.24em] text-[color:var(--tr-ink-soft)]">
                    {p.year}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
                  {p.name}
                </h3>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
